import React, { useState, useEffect } from 'react';
import { Settings, DollarSign, TrendingUp, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import { firestoreService } from '../services/firestoreService';
import { AdminAction } from '../types';
import { formatDate, formatCurrency } from '../utils';

export default function Actions() {
  const [actions, setActions] = useState<AdminAction[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchActions = async () => {
      try {
        const actions = await firestoreService.getAdminActions();
        setActions(actions);
      } catch (error) {
        console.error('Failed to fetch actions:', error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchActions();
  }, []);

  const getActionIcon = (type: string) => {
    switch (type) {
      case 'promote':
        return <TrendingUp className="h-5 w-5 text-green-500" />;
      case 'refund':
        return <DollarSign className="h-5 w-5 text-yellow-500" />;
      case 'suspend':
        return <AlertTriangle className="h-5 w-5 text-red-500" />;
      case 'activate':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'delete':
        return <XCircle className="h-5 w-5 text-red-500" />;
      default:
        return <Settings className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 bg-green-100';
      case 'pending':
        return 'text-yellow-600 bg-yellow-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'promote':
        return 'text-green-600 bg-green-100';
      case 'refund':
        return 'text-yellow-600 bg-yellow-100';
      case 'suspend':
        return 'text-red-600 bg-red-100';
      case 'activate':
        return 'text-blue-600 bg-blue-100';
      case 'delete':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Admin Actions</h1>
        <p className="mt-1 text-sm text-gray-500">
          Track and manage administrative actions performed on user accounts
        </p>
      </div>

      {/* Actions Table */}
      <div className="bg-white shadow overflow-hidden sm:rounded-md">
        <ul className="divide-y divide-gray-200">
          {actions.map((action) => (
            <li key={action.id}>
              <div className="px-4 py-4 sm:px-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      {getActionIcon(action.type)}
                    </div>
                    <div className="ml-4">
                      <div className="flex items-center">
                        <p className="text-sm font-medium text-gray-900">
                          {action.description}
                        </p>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(action.type)}`}>
                          {action.type}
                        </span>
                        <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(action.status)}`}>
                          {action.status}
                        </span>
                      </div>
                      <div className="mt-1">
                        <p className="text-sm text-gray-500">
                          User: {action.userEmail}
                        </p>
                        {action.amount && (
                          <p className="text-sm text-gray-500">
                            Amount: {formatCurrency(action.amount, action.currency || 'USD')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-900">
                      {formatDate(action.createdAt)}
                    </p>
                    <p className="text-sm text-gray-500">
                      by {action.adminEmail}
                    </p>
                  </div>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {actions.length === 0 && (
        <div className="text-center py-12">
          <Settings className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No actions</h3>
          <p className="mt-1 text-sm text-gray-500">
            No administrative actions have been performed yet.
          </p>
        </div>
      )}
    </div>
  );
} 